/* eslint-disable indent */
import { html, css, LitElement } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { connect } from 'pwa-helpers/connect-mixin.js';
import { store } from '../../store/store.js';
import sharedStyles from '../../app/styles.js';
import {
    saveLtiAdvantageItem,
    updateLtiAdvantageItem,
    updateLtiAdvantageItemParameters,
} from '../../store/ltiAdvantage/ltiAdvantage.js';
import { deploymentOptionLabel } from '../../store/ltiAdvantage/deployments.js';
import { navigateBack } from '../../store/route/route.js';
import { previewElearning } from '../../store/preview/preview.js';
import { translate } from '../../helpers/translate.js';
import { launchTypes } from '../../store/elearning/helpers.js';
import '@sdb-develop/learning-frontend';
import '@vaadin/select/theme/material/vaadin-select.js';
import '@polymer/paper-input/paper-input.js';
import '@polymer/iron-form/iron-form.js';
import '@polymer/paper-toggle-button/paper-toggle-button.js';
import '../shared/sdb-loading.js';
import '../shared/sdb-parameters.js';
import '../shared/sdb-overlay-layout.js';
import '../shared/sdb-status-message.js';
import './sdb-lti-advantage-deployments-dialog.js';

const toNullableInt = (value) => {
    const trimmed = `${value ?? ''}`.trim();
    return trimmed === '' ? null : parseInt(trimmed, 10);
};

/**
 * Detail view of an LTI 1.3 item: the ElearningItem (CompositionApi) and its resource link in the
 * LTI Advantage Platform, edited together and saved with one action (see saveLtiAdvantageItem).
 */
@customElement('sdb-lti-advantage-item')
class SdbLtiAdvantageItem extends connect(store)(LitElement) {

    @property({ type: String })
    accessor targetId;

    @state()
    accessor item;

    @state()
    accessor deployments = [];

    @state()
    accessor isLoading = false;

    @state()
    accessor isDirty = false;

    @state()
    accessor hasGlobalId = false;

    // the module has a launch URL of its own instead of the supplier's default; off until switched on
    @state()
    accessor hasUniqueLaunchUrl = false;

    // item the switch was last initialised for, so it follows the stored value only when another item opens
    _launchUrlTargetId;

    @query('#lti_advantage_item_form')
    accessor itemForm;

    @query('#deployments_dialog')
    accessor deploymentsDialog;

    get resourceLink() {
        return this.item?.resourceLink;
    }

    get selectedDeployment() {
        return this.deployments.find((d) => d.contextId === this.resourceLink?.contextId);
    }

    get deploymentItems() {
        const items = this.deployments.map((d) => ({ value: d.contextId, label: deploymentOptionLabel(d, translate('deploymentNotConfigured')) }));

        // a link of a deployment that is not in the list (should not happen for the own portal) stays visible
        if (this.resourceLink?.contextId && !this.selectedDeployment) {
            items.unshift({ value: this.resourceLink.contextId, label: this.resourceLink.contextId, disabled: true });
        }

        return items;
    }

    get launchUrlPlaceholder() {
        return this.selectedDeployment?.redirectUrl || this.resourceLink?.effectiveLaunchUrl || '';
    }

    stateChanged(state) {
        this.item = state.ltiAdvantage?.current;
        this.isLoading = state.ltiAdvantage?.status === 'loading' || !state.ltiAdvantage?.current;
        this.isDirty = state.ltiAdvantage?.dirty;
        this.deployments = state.ltiAdvantageDeployments?.list || [];
        this.hasGlobalId = state.env.hasGlobalId;

        if (this.item && this.item.targetId !== this._launchUrlTargetId) {
            this._launchUrlTargetId = this.item.targetId;
            this.hasUniqueLaunchUrl = !!this.item.resourceLink?.launchUrl;
        }
    }

    static get styles() {
        return [
            sharedStyles,
            css`
                [slot=content] {
                    width: 100%;
                    padding: 0 20px;
                    max-width: 1400px;
                }

                paper-input, sdb-content-checkbox {
                    margin-bottom: 5px;
                }

                paper-input.small {
                    width: 350px;
                }

                vaadin-select {
                    width: 40%;
                    min-width: 200px;
                }

                .deployment-row {
                    display: flex;
                    align-items: flex-end;
                }

                .deployment-row vaadin-select {
                    width: 60%;
                }

                .deployment-row sdb-content-iconbutton {
                    margin-left: 10px;
                    margin-bottom: 4px;
                }

                .hint {
                    font-size: 12px;
                    color: #888;
                    margin: -4px 0 12px 0;
                }

                #unique_launch_url_toggle {
                    margin: 16px 0 4px 0;
                }

                .section-title {
                    font-size: 14px;
                    font-weight: 500;
                    margin: 20px 0 8px 0;
                }
            `,
        ];
    }

    render() {
        if (this.isLoading || this.targetId !== this.item.targetId) {
            return html`<sdb-loading></sdb-loading>`;
        }

        const item = this.item;
        const link = this.resourceLink;
        const update = (changes) => store.dispatch(updateLtiAdvantageItem({ changes }));
        const updateLink = (changes) => update({ resourceLink: changes });

        return html`
            <sdb-overlay-layout
                headline=${item.title}
                ?dirty=${this.isDirty}
                @back=${() => { store.dispatch(navigateBack('lti-advantage')); }}>

                <div slot="buttons">
                    <sdb-content-button
                        id="preview_button"
                        label=${translate('preview')}
                        primary
                        ?disabled=${this.isDirty}
                        @click=${() => { store.dispatch(previewElearning({ item: item })); }}>
                    </sdb-content-button>
                    <sdb-content-button
                        id="save_button"
                        label=${translate('save')}
                        primary
                        ?disabled=${!this.isDirty}
                        @click=${() => {
                            if (this.itemForm.validate()) {
                                store.dispatch(saveLtiAdvantageItem());
                            }
                        }}>
                    </sdb-content-button>
                </div>

                <iron-form id="lti_advantage_item_form" slot="content">
                    <form>
                        <sdb-layout-100>

                            <sdb-content-card id="general_card">
                                <paper-input
                                    id="name_input"
                                    transparent
                                    label=${`${translate('name')} *`}
                                    auto-validate
                                    required
                                    error-message=${translate('errRequired')}
                                    value=${item.title ?? ''}
                                    @change=${(e) => update({ title: e.target.value.trim() })}>
                                </paper-input>

                                <paper-input
                                    id="description_textarea"
                                    transparent
                                    label=${translate('description')}
                                    value=${item.description ?? ''}
                                    @change=${(e) => update({ description: e.target.value })}>
                                </paper-input>

                                <vaadin-select
                                    id="launchtype_select"
                                    transparent
                                    label=${translate('launchType')}
                                    value=${String(item.launchType ?? 0)}
                                    @change=${(e) => { update({ launchType: parseInt(e.target.value, 10) }); }}
                                    .items=${Object.entries(launchTypes).map(([key, value]) => ({
                                        label: translate(key.toLowerCase()),
                                        value: String(value),
                                    }))}>
                                </vaadin-select>
                            </sdb-content-card>

                            <sdb-content-card id="lti_card">
                                ${!link
                                    ? html`<sdb-status-message label=${translate('resourceLinkUnavailable')}></sdb-status-message>`
                                    : html`
                                        <div class="deployment-row">
                                            <vaadin-select
                                                id="deployment_select"
                                                transparent
                                                label=${`${translate('deployment')} *`}
                                                value=${link.contextId ?? ''}
                                                .items=${this.deploymentItems}
                                                @change=${(e) => { updateLink({ contextId: e.target.value }); }}>
                                            </vaadin-select>
                                            <sdb-content-iconbutton
                                                id="manage_deployments_button"
                                                icon="fal fa-plug"
                                                size="24"
                                                title=${translate('manageDeployments')}
                                                ?hidden=${this.hasGlobalId}
                                                @click=${() => { this.deploymentsDialog.open(); }}>
                                            </sdb-content-iconbutton>
                                        </div>

                                        <paper-toggle-button
                                            id="unique_launch_url_toggle"
                                            ?checked=${this.hasUniqueLaunchUrl}
                                            @change=${(e) => this._setUniqueLaunchUrl(e.target.checked)}>
                                            <span>${translate('uniqueLaunchUrl')}</span>
                                        </paper-toggle-button>
                                        ${this.hasUniqueLaunchUrl ? html`
                                            <paper-input
                                                id="launch_url_input"
                                                transparent
                                                type="url"
                                                label=${`${translate('launchUrl')} *`}
                                                placeholder=${this.launchUrlPlaceholder}
                                                required
                                                auto-validate
                                                error-message=${translate('errRequired')}
                                                value=${link.launchUrl ?? ''}
                                                @change=${(e) => updateLink({ launchUrl: e.target.value.trim() || null })}>
                                            </paper-input>
                                        ` : html`<div class="hint">${translate('launchUrlHelper')}</div>`}

                                        <div class="section-title">${translate('customParameters')}</div>
                                        <div class="hint">${translate('customParametersHelper')}</div>
                                        <sdb-parameters
                                            id="parameters"
                                            .parameters=${link.customParams || {}}
                                            @changed=${(e) => store.dispatch(updateLtiAdvantageItemParameters(e.detail.value))}>
                                        </sdb-parameters>
                                    `
                                }
                            </sdb-content-card>

                            ${link ? html`
                                <sdb-content-collapsablecard
                                    id="settings_card"
                                    headline=${translate('advancedSettings')}
                                    icon="fal fa-sliders-up"
                                    headlineSize="16px"
                                    iconSize="32">

                                    <sdb-layout-50-50>
                                        <div slot="left">
                                            <paper-input
                                                id="masteryscore_input"
                                                class="small"
                                                transparent
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="1"
                                                label=${translate('masteryScore')}
                                                value=${link.masteryScore ?? ''}
                                                auto-validate
                                                error-message=${translate('errChooseBetween0_100')}
                                                @change=${(e) => {
                                                    if (!e.target.invalid) {
                                                        updateLink({ masteryScore: toNullableInt(e.target.value) });
                                                    }
                                                }}>
                                            </paper-input>
                                            <div class="hint">${translate('masteryScoreHelper')}</div>
                                        </div>
                                        <div slot="right">
                                            <paper-input
                                                id="maxattempts_input"
                                                class="small"
                                                transparent
                                                type="number"
                                                min="1"
                                                step="1"
                                                label=${translate('maxAttempts')}
                                                value=${link.maxAttempts ?? ''}
                                                auto-validate
                                                error-message=${translate('errInvalidNumber')}
                                                @change=${(e) => {
                                                    if (!e.target.invalid) {
                                                        updateLink({ maxAttempts: toNullableInt(e.target.value) });
                                                    }
                                                }}>
                                            </paper-input>
                                            <div class="hint">${translate('maxAttemptsHelper')}</div>
                                        </div>
                                    </sdb-layout-50-50>
                                </sdb-content-collapsablecard>
                            ` : ''}

                        </sdb-layout-100>
                    </form>
                </iron-form>
            </sdb-overlay-layout>

            <sdb-lti-advantage-deployments-dialog id="deployments_dialog"></sdb-lti-advantage-deployments-dialog>
        `;
    }

    // Switching off drops the module's own launch URL, so the supplier's default applies again.
    async _setUniqueLaunchUrl(on) {
        this.hasUniqueLaunchUrl = on;

        if (!on && this.resourceLink?.launchUrl) {
            store.dispatch(updateLtiAdvantageItem({ changes: { resourceLink: { launchUrl: null } } }));
        }

        if (on) {
            await this.updateComplete;
            this.renderRoot.querySelector('#launch_url_input')?.focus();
        }
    }

    firstUpdated() {
        this.dispatchEvent(
            new CustomEvent('fullscreen', {
                bubbles: true,
                composed: true,
                detail: {
                    fullscreen: true,
                },
            }),
        );
    }
}
